import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, AlertTriangle, Info } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

// crude but reasonable: strip HTML, split words, ~500 words per single-spaced page
const WORDS_PER_PAGE = 500;

function stripHtml(html) {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || "";
}
function wc(html) { return stripHtml(html).trim().split(/\s+/).filter(Boolean).length; }

export default function CompliancePanel({ proposal, agencyKey, onAgencyChange }) {
  const [agencies, setAgencies] = useState({});
  useEffect(() => { api.get("/compliance/agencies").then(r=>setAgencies(r.data)); }, []);

  const totalWords = useMemo(() => {
    return Object.values(proposal?.sections || {}).reduce((s, v) => s + wc(v), 0);
  }, [proposal?.sections]);
  const totalPages = Math.max(1, Math.ceil(totalWords / WORDS_PER_PAGE));
  const preset = agencies[agencyKey] || null;

  const checks = [];
  if (preset) {
    const l = preset.limits || {};
    if (l.total_pages) {
      const over = totalPages > l.total_pages;
      checks.push({ label: `Total pages ≤ ${l.total_pages}`, actual: `${totalPages} pages · ${totalWords} words`, ok: !over, warn: totalPages >= l.total_pages - 1 && !over });
    }
    if (l.abstract_words) {
      const w = wc(proposal?.sections?.abstract);
      checks.push({ label: `Abstract ≤ ${l.abstract_words} words`, actual: `${w} words`, ok: w <= l.abstract_words, warn: w >= l.abstract_words * 0.9 && w <= l.abstract_words });
    }
    if (l.objectives_pages) {
      const p = Math.max(1, Math.ceil(wc(proposal?.sections?.objectives) / WORDS_PER_PAGE));
      checks.push({ label: `Objectives ≤ ${l.objectives_pages} page(s)`, actual: `${p} page(s)`, ok: p <= l.objectives_pages });
    }
    if (l.methodology_pages) {
      const p = Math.max(1, Math.ceil(wc(proposal?.sections?.methodology) / WORDS_PER_PAGE));
      checks.push({ label: `Methodology ≤ ${l.methodology_pages} pages`, actual: `${p} pages`, ok: p <= l.methodology_pages });
    }
  }

  return (
    <div className="card-crisp rounded-lg p-4" data-testid="compliance-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-xl">Compliance</div>
      </div>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">Agency preset</Label>
          <Select value={agencyKey || "Generic / Other"} onValueChange={onAgencyChange}>
            <SelectTrigger data-testid="compliance-agency"><SelectValue/></SelectTrigger>
            <SelectContent>{Object.keys(agencies).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {preset && (
          <div className="text-xs text-muted-foreground flex items-start gap-1.5"><Info className="w-3 h-3 mt-0.5"/><span>{preset.notes}</span></div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-md border border-border p-3 text-center">
            <div className="overline">Total pages</div>
            <div className="font-display text-3xl">{totalPages}</div>
          </div>
          <div className="rounded-md border border-border p-3 text-center">
            <div className="overline">Total words</div>
            <div className="font-display text-3xl">{totalWords.toLocaleString()}</div>
          </div>
        </div>
        {checks.length > 0 && (
          <div className="space-y-1.5">
            {checks.map((c, i) => (
              <div key={i} className={`flex items-start gap-2 text-xs p-2 rounded-md border ${c.ok ? (c.warn ? "border-amber-300 bg-amber-50 text-amber-900" : "border-emerald-200 bg-emerald-50 text-emerald-900") : "border-red-300 bg-red-50 text-red-900"}`} data-testid={`compliance-check-${i}`}>
                {c.ok ? <CheckCircle2 className="w-4 h-4 flex-shrink-0"/> : <AlertTriangle className="w-4 h-4 flex-shrink-0"/>}
                <div className="flex-1">
                  <div className="font-medium">{c.label}</div>
                  <div className="opacity-80">{c.actual}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
